import { cn } from '@listening/utils/react-helpers';
import * as m from '@/paraglide/messages';
import { Fragment } from 'react/jsx-runtime';

type ContentItem = {
  text: string;
  isMain?: boolean; // true by default
  citation?: string;
  additionalText?: string;
  isHeader?: boolean; // false by default
  isClickable?: boolean;
};

type Card = {
  title: () => string;
  content: Array<ContentItem>;
};

function getCards(): Array<Card> {
  return [
    {
      title: () => m.homepage_advantages_citations(),
      content: [
        {
          text: m.advantages_sample_classifiers(),
          citation: m.advantages_sample_citation_1(),
        },
        {
          text: m.advantages_sample_formulation(),
        },
        {
          text: m.advantages_sample_multilabel(),
          citation: m.advantages_sample_citation_2(),
        },
      ],
    },
    {
      title: () => m.homepage_advantages_footnotes(),
      content: [
        {
          text: m.advantages_sample_classifiers(),
        },
        {
          text: m.advantages_sample_open_images(),
          isMain: false,
        },
        {
          text: m.advantages_sample_overlapping(),
          isMain: false,
        },
      ],
    },
    {
      title: () => m.homepage_advantages_references(),
      content: [
        {
          text: m.advantages_sample_classifiers_short(),
        },
        {
          text: m.advantages_sample_ref_heading(),
          isHeader: true,
        },
        {
          text: m.advantages_sample_ref_1(),
          isMain: false,
        },
        {
          text: m.advantages_sample_ref_2(),
          isMain: false,
        },
      ],
    },
  ];
}

// Helper function to determine if content is main (true by default)
const isMainContent = (item: ContentItem): boolean => item.isMain !== false;

export function Advantages() {
  const cards = getCards();
  return (
    <section
      aria-labelledby='advantages-heading'
      className='mx-auto max-w-7xl px-4 py-16'
    >
      <h2
        className='mx-auto mb-16 max-w-4xl text-center text-2xl leading-tight font-bold sm:text-5xl lg:text-6xl'
        id='advantages-heading'
      >
        {m.homepage_advantages_heading()}
      </h2>

      <div className='grid gap-6 lg:grid-cols-3' role='list'>
        {cards.map((card, cardIndex) => (
          <div className='mb-12 lg:mb-0' key={card.title()} role='listitem'>
            <div
              aria-labelledby={`card-title-${String(cardIndex)}`}
              className='mx-4 h-full rounded-2xl border-12 border-steel-140 bg-white px-4 py-2 transition-all duration-300 hover:shadow-lg sm:mx-0 sm:px-8 sm:py-4'
            >
              {card.content.map((item) => (
                <Fragment key={item.text}>
                  {item.isHeader ? (
                    <p
                      aria-level={3}
                      className='font-semibold sm:mt-4'
                      role='heading'
                    >
                      {item.text}
                    </p>
                  ) : (
                    <p
                      aria-hidden={!isMainContent(item)}
                      className={cn(
                        'mt-2',
                        isMainContent(item)
                          ? 'text-lg text-navy-800'
                          : 'text-xs text-azure-595 line-through lg:text-sm',
                        { 'mt-0': item.isHeader },
                      )}
                    >
                      <span className='text-xs leading-[220%] lg:text-sm'>
                        {item.text.split(' ').map((word, idx) => (
                          <span
                            className='cursor-default transition-colors duration-200 hover:text-azure-595'
                            key={`${word}-${idx.toString()}`}
                          >
                            {word}{' '}
                          </span>
                        ))}
                      </span>
                      {item.citation && (
                        <span className='text-xs leading-[220%] text-azure-595 line-through lg:text-sm'>
                          {item.citation.split(' ').map((word, idx) => (
                            <span
                              className='cursor-default transition-colors duration-200 hover:text-azure-595'
                              key={`${word}-${idx.toString()}`}
                            >
                              {word}{' '}
                            </span>
                          ))}
                        </span>
                      )}
                      {item.additionalText && (
                        <span className='text-xs leading-[220%] text-navy-800 lg:text-sm'>
                          {item.additionalText.split(' ').map((word, idx) => (
                            <span
                              className='cursor-default transition-colors duration-200 hover:text-azure-595'
                              key={`${word}-${idx.toString()}`}
                            >
                              {word}{' '}
                            </span>
                          ))}
                        </span>
                      )}
                    </p>
                  )}
                </Fragment>
              ))}
            </div>
            <div
              className='mt-4 px-4 text-center text-xl font-semibold sm:px-0'
              id={`card-title-${String(cardIndex)}`}
            >
              {card.title()}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
